import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { parseISO, format, addHours } from 'date-fns';
import { useAuth } from '../../hooks/auth';
import { useClinica } from '../../hooks/clinica';
import { IMessage, useChat, IUser } from '../../hooks/chat';
import Button from '../Button';

import socket from '../../services/socket';

import pic from '../../assets/pic.png';

import {
  Container,
  ListaUsuarios,
  Mensagens,
  ContainerMensagens,
  MessageForm,
  FloatingLabel,
  LabelMessage,
  Avatar,
  Mensagem,
  Texto,
  Nome,
} from './styles';

import api from '../../services/api';
import Input from '../Input';
import Loading from '../Loading';
import { useToast } from '../../hooks/toast';
import getResponseErrors from '../../utils/getResponseErrors';

export interface ISendMessage {
  clinica: string;
  from: string;
  idFrom: number;
  to: string;
  idTo: number;
  mensagem: string;
  lida: boolean;
  minha?: boolean;
  created_at: Date;
  painel?: boolean;
  audioUrl?: string;
}

type ISendMessageOmit = Omit<ISendMessage, 'from'>;
export interface ISendFormatada extends ISendMessageOmit {
  id: number;
  from: {
    UsuCodigo: number;
    UsuNome: string;
    UsuEmail: string;
    avatar_url: string;
    UsuTipo: number;
    UsuClinica: number;
  };
  dataFormatada: string;
}

type Props = {
  setTitulo(titulo: string): void;
};

const Chat: React.FC<Props> = ({ setTitulo }) => {
  const formRefChat = useRef<FormHandles>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const containerMessageRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToast();
  const { user } = useAuth();
  const { optionsClinica } = useClinica();
  const {
    users,
    usuarioSelecionado,
    mensagens,
    setUsuarioSelecionado,
    buscarUsuarios,
    addMensagens,
    addMensagem,
    addOldMensagem,
  } = useChat();
  const [loading, setLoading] = useState(false);

  const [focus, setFocus] = useState(false);

  const [scrollTop, setScrollTop] = useState(false);

  const [pagina, setPagina] = useState(1);
  const [carregouTodas, setCarregouTodas] = useState(false);

  // running on mount
  useEffect(() => {
    const onScroll = (): void => {
      // Logic is false tell user reaches threshold, then true after.
      const scrollCheck =
        containerMessageRef.current !== null &&
        containerMessageRef.current.scrollTop <= 0;
      if (scrollCheck !== scrollTop) {
        setScrollTop(scrollCheck);
      }
    };

    // setting the event handler from web API
    containerMessageRef.current?.addEventListener('scroll', onScroll);

    // cleaning up from the web API
    return () => {
      containerMessageRef.current?.removeEventListener('scroll', onScroll);
    };
  }, [scrollTop, setScrollTop]);

  useEffect(() => {
    if (!users || users.length === 0) buscarUsuarios(user.UsuCodigo);
  }, [buscarUsuarios, setUsuarioSelecionado, users, user.UsuCodigo]);

  const loadOldMensages = useCallback(async (): Promise<void> => {
    try {
      if (usuarioSelecionado && !carregouTodas) {
        setLoading(true);
        const response = await api.get(
          `/mensagem/conversa/${user.UsuCodigo}/${
            usuarioSelecionado.UsuCodigo
          }?limite=10&pagina=${pagina + 1}`,
        );
        const mensagensFormatada: ISendFormatada[] = response.data.map(
          (mens: IMessage) => {
            const {
              UsuCodigo,
              UsuNome,
              UsuEmail,
              avatar_url,
              UsuTipo,
              UsuClinica,
            } = mens.from;
            return {
              ...mens,
              from: {
                UsuCodigo,
                UsuNome,
                UsuEmail,
                avatar_url,
                UsuTipo,
                UsuClinica,
              },
              minha: UsuCodigo === user.UsuCodigo,
              dataFormatada: mens.created_at
                ? format(
                    addHours(parseISO(mens.created_at.toString()), -3),
                    'dd/MM HH:mm',
                  )
                : '',
            };
          },
        );
        setPagina(pagina + 1);
        addOldMensagem(mensagensFormatada);
        setCarregouTodas(mensagensFormatada.length < 10);
      }
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível carregar mensagens',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
    if (containerMessageRef.current) {
      containerMessageRef.current.style.overflow = 'auto';
    }
  }, [
    user.UsuCodigo,
    usuarioSelecionado,
    addOldMensagem,
    pagina,
    carregouTodas,
    addToast,
  ]);

  useEffect(() => {
    if (scrollTop && !carregouTodas && mensagens && mensagens.length > 0) {
      loadOldMensages();
      if (containerMessageRef.current) {
        containerMessageRef.current.scrollTo(0, 15);
        containerMessageRef.current.style.overflow = 'hidden';
      }
    }
  }, [loadOldMensages, scrollTop, carregouTodas, mensagens]);

  useEffect(() => {
    if (pagina === 1)
      messageRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens, pagina]);

  useEffect(() => {
    if (!usuarioSelecionado) addMensagens(undefined);
  }, [addMensagens, usuarioSelecionado]);

  const handleConversar = useCallback(
    async (usuario: IUser): Promise<void> => {
      setTitulo(`Conversando com ${usuario.UsuNome}`);
      setUsuarioSelecionado(usuario);
      try {
        const response = await api.get(
          `/mensagem/conversa/${user.UsuCodigo}/${usuario.UsuCodigo}?limite=10&pagina=1`,
        );
        const mensagensFormatada: ISendFormatada[] = response.data.map(
          (mens: IMessage) => {
            const {
              UsuCodigo,
              UsuNome,
              UsuEmail,
              avatar_url,
              UsuTipo,
              UsuClinica,
            } = mens.from;
            return {
              ...mens,
              from: {
                UsuCodigo,
                UsuNome,
                UsuEmail,
                avatar_url,
                UsuTipo,
                UsuClinica,
              },
              minha: UsuCodigo === user.UsuCodigo,
              dataFormatada: mens.created_at
                ? format(
                    addHours(parseISO(mens.created_at.toString()), -3),
                    'dd/MM HH:mm',
                  )
                : '',
            };
          },
        );
        addMensagens(mensagensFormatada);
        setPagina(1);
        setCarregouTodas(mensagensFormatada.length < 10);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar mensagens',
          type: 'error',
          description: mensagemErro,
        });
      }
    },
    [
      setTitulo,
      user,
      addMensagens,
      setUsuarioSelecionado,
      setCarregouTodas,
      addToast,
    ],
  );

  const handleSendMessage = useCallback(
    data => {
      if (data.texto !== '') {
        const clinica =
          optionsClinica.find(cli => cli.value === user.UsuClinica.toString())
            ?.label || 'Sem Clinica';
        const mensagem: ISendMessage = {
          clinica,
          from: user.UsuNome,
          idFrom: user.UsuCodigo,
          to: usuarioSelecionado?.UsuNome || '',
          idTo: usuarioSelecionado ? Number(usuarioSelecionado.UsuCodigo) : 0,
          mensagem: data.texto,
          lida: false,
          created_at: new Date(),
        };
        socket.emit('actionClient', mensagem);

        const novaMensagem: ISendFormatada = {
          ...mensagem,
          clinica: user.UsuClinica.toString(),
          from: user,
          minha: true,
          dataFormatada: format(mensagem.created_at, 'dd/MM/yy HH:mm'),
          id: Math.random() * 999999999,
        };
        addMensagem(novaMensagem);
        formRefChat.current?.setFieldValue('texto', '');
        messageRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [user, usuarioSelecionado, addMensagem, optionsClinica],
  );
  return (
    <Container className="container">
      {loading && <Loading />}
      <Row>
        <Col md={3}>
          <ListaUsuarios>
            {users.length > 0 &&
              users.map(usuario => (
                <button
                  type="button"
                  className="list-group-item list-group-item-action  d-flex justify-content-between py-1 px-2"
                  onClick={() => handleConversar(usuario)}
                  key={`chatBotao-${usuario.UsuCodigo}`}
                >
                  {usuario.UsuNome.padEnd(15, ' ').substr(0, 14)}
                  <span
                    className={`badge badge-primary badge-pill ${
                      usuario.naoLidas ? '' : 'd-none'
                    }`}
                  >
                    {usuario.naoLidas}
                  </span>
                </button>
              ))}
          </ListaUsuarios>
        </Col>
        <Col md={9}>
          <div
            className="border rounded mt-0"
            style={{ height: '100%', paddingBottom: '0' }}
          >
            <ContainerMensagens>
              <Mensagens ref={containerMessageRef}>
                {mensagens &&
                  mensagens.map(mensagem => (
                    <Mensagem
                      minha={mensagem.minha}
                      key={`mensagen-${mensagem.id}`}
                    >
                      <div
                        style={{
                          display: 'table-cel',
                          alignItems: mensagem.minha ? 'end' : 'start',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                          }}
                        >
                          <Avatar minha={!mensagem.minha}>
                            <img src={pic} alt="teste" />
                          </Avatar>
                          <Texto minha={mensagem.minha}>
                            {mensagem.mensagem}
                          </Texto>

                          <Avatar minha={mensagem.minha}>
                            <img src={pic} alt="teste" />
                          </Avatar>
                        </div>
                        <Nome minha={mensagem.minha}>
                          {mensagem.from.UsuNome} {mensagem.dataFormatada}
                        </Nome>
                      </div>
                    </Mensagem>
                  ))}
                <div ref={messageRef} />
              </Mensagens>

              <MessageForm>
                <Form
                  ref={formRefChat}
                  onSubmit={handleSendMessage}
                  style={{ width: '100%' }}
                >
                  <input type="hidden" value="prayer" />
                  <Row>
                    <Col md={10}>
                      <FloatingLabel>
                        <Input
                          name="texto"
                          style={{
                            border: 'none',
                            borderBottom: '1px solid rgba(0,0,0,.12)',
                            display: 'block',
                            fontSize: '16px',
                            fontFamily: 'Helvetica,Arial,sans-serif',
                            margin: '0',
                            padding: '4px 0',
                            width: '100%',
                            background: '0 0',
                            textAlign: 'left',
                            color: 'inherit',
                          }}
                          type="text"
                          id="message"
                          onFocus={() => setFocus(true)}
                          onBlur={() => setFocus(false)}
                          disabled={!usuarioSelecionado}
                          autoComplete="off"
                        />
                        <LabelMessage focus={focus}>Mensagem...</LabelMessage>
                      </FloatingLabel>
                    </Col>
                    <Col md={2}>
                      <Button
                        className="mt-4 ml-4"
                        style={{ height: '35px' }}
                        type="submit"
                        disabled={!usuarioSelecionado}
                      >
                        Send
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </MessageForm>
              <div id="user-form" />
            </ContainerMensagens>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Chat;
